/**
 * ModalActions — 모달 하단 버튼 줄. 순서와 "채워진 primary 1개" 규칙을 코드로 강제한다.
 *
 * <ModalActions
 *   primary={{ label: '삭제하기', onClick, loading, danger, disabled, type, ref, testId }}
 *   secondary={{ label: '취소', onClick, disabled }}
 *   tertiary={{ label: '나중에 볼게요', onClick, disabled }}   // 텍스트 버튼
 * />
 *
 * 데스크톱(>600px): [tertiary(왼쪽 텍스트)] ……… [secondary][primary]
 * 모바일(≤600px) : 세로 스택·전체 폭, primary(48px)가 맨 위 → secondary → tertiary
 *
 * - 라벨은 동사형("채점하기", "삭제하기")으로 넘긴다. "확인"처럼 모호한 라벨은 쓰지 않는다.
 * - loading이면 primary 안에 스피너가 들어가고 모든 버튼이 잠긴다.
 * - danger는 파괴적 동작(삭제·해지)에만 쓴다. 색만 바뀌고 위치·크기는 같다.
 * - 각 액션의 testId를 넘기면 기본값(`${testId}-primary` 등) 대신 그 값을 쓴다 (기존 테스트·E2E 셀렉터 유지용).
 */

import React from 'react';
import { useSheetLayout } from './Dialog';
import { Spinner } from './AsyncState';
import './ModalActions.css';

const isAction = (action) => Boolean(action && typeof action === 'object' && action.label);

const joinClassNames = (...values) => values.filter(Boolean).join(' ');

const ActionButton = ({ action, className, kind, locked, testId }) => {
  if (!isAction(action)) return null;
  const loading = kind === 'primary' && Boolean(action.loading);
  const disabled = Boolean(action.disabled) || locked;
  const autoFocusProps = action.autoFocus ? { autoFocus: true } : {};
  return (
    <button
      aria-busy={loading || undefined}
      className={joinClassNames(
        'whatquiz-modal-actions__btn',
        `whatquiz-modal-actions__btn--${kind}`,
        kind === 'primary' && action.danger && 'whatquiz-modal-actions__btn--danger',
        className,
      )}
      data-testid={action.testId || testId}
      disabled={disabled}
      onClick={action.onClick}
      ref={action.ref}
      type={action.type || 'button'}
      {...autoFocusProps}
    >
      {loading && <Spinner size={16} tone="inverse" thickness={2} className="whatquiz-modal-actions__spinner" />}
      <span>{action.label}</span>
    </button>
  );
};

const ModalActions = ({
  className,
  primary = null,
  secondary = null,
  tertiary = null,
  testId = 'modal-actions',
}) => {
  const isStacked = useSheetLayout(true);
  const locked = Boolean(primary?.loading);

  const primaryButton = (
    <ActionButton action={primary} key="primary" kind="primary" locked={locked} testId={`${testId}-primary`} />
  );
  const secondaryButton = (
    <ActionButton action={secondary} key="secondary" kind="secondary" locked={locked} testId={`${testId}-secondary`} />
  );
  const tertiaryButton = (
    <ActionButton action={tertiary} key="tertiary" kind="tertiary" locked={locked} testId={`${testId}-tertiary`} />
  );

  // 탭 순서 = 시각 순서. 모바일은 primary가 맨 위, 데스크톱은 왼쪽 텍스트 → 오른쪽 primary.
  const ordered = isStacked
    ? [primaryButton, secondaryButton, tertiaryButton]
    : [tertiaryButton, secondaryButton, primaryButton];

  return (
    <div
      className={joinClassNames(
        'whatquiz-modal-actions',
        isStacked ? 'whatquiz-modal-actions--stacked' : 'whatquiz-modal-actions--row',
        className,
      )}
      data-testid={testId}
    >
      {ordered}
    </div>
  );
};

export default ModalActions;
